/**
 * Register the PWA service worker in production only. In dev, service workers
 * can interfere with hot-reload and dev server proxies.
 *
 * Anti-stale-bundle guarantees:
 *   1. `updateViaCache: 'none'` — the SW file is ALWAYS fetched from the
 *      network on every registration, bypassing the browser's 24h SW cache.
 *   2. `reg.update()` runs on window focus + every 15 min so a long-lived
 *      tab picks up new deploys quickly.
 *   3. `controllerchange` reloads the page once (guarded) as soon as a new
 *      SW takes control, so the user is bumped onto the fresh bundle
 *      without a manual hard-refresh.
 */
export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  if (process.env.NODE_ENV !== 'production') return;

  const REGISTER = () => {
    navigator.serviceWorker
      .register('/service-worker.js', { scope: '/', updateViaCache: 'none' })
      .then((reg) => {
        // If a new SW is already waiting on first load, activate it immediately.
        if (reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        // Whenever a new SW is found, tell it to skip waiting so it activates.
        reg.addEventListener('updatefound', () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener('statechange', () => {
            if (nw.state === 'installed' && navigator.serviceWorker.controller) {
              nw.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
        // Periodic + focus-triggered update checks.
        const check = () => { try { reg.update(); } catch (_) {} };
        window.addEventListener('focus', check);
        setInterval(check, 15 * 60 * 1000);
      })
      .catch((err) => console.warn('SW registration failed:', err));
  };

  // Reload once when a new SW takes over, so the user lands on the fresh bundle.
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });

  if (document.readyState === 'complete') REGISTER();
  else window.addEventListener('load', REGISTER);
}
